import { COMPARE_VERSIONS } from "./utilityHelpers";
import {
    MAGENTO_COMMERCE_TYPES,
    COMMERCE_LEVELS_ARRAY,
    PRODUCT_PLATFORM,
} from "../constants/AppConstants";

export const getVersionCheckboxValues = (checkList, list) => {
    return checkList.reduce((acc, value, index) => {
        if (value) {
            acc.push(list[index]);
        }
        return acc;
    }, []);
};

// converts the form values to a compatable shape for the backend
export const buildVersionCompatibilityJson = (
    productType,
    platform,
    versionCompatibilityData,
    eeList,
    ceList,
    eceList
) => {
    const ceListArr = Object.values(ceList);
    const eeListArr = Object.values(eeList);
    const eceListArr = Object.values(eceList);

    const ce = getVersionCheckboxValues(versionCompatibilityData.CE,   ceListArr);
    const ee = getVersionCheckboxValues(versionCompatibilityData.EE,   eeListArr);
    const ece = getVersionCheckboxValues(versionCompatibilityData.ECE, eceListArr);
    let version_compatibility;

    if (platform === PRODUCT_PLATFORM.M2) {
        version_compatibility = [
            {
                edition: "EE",
                versions: ee,
            },
            {
                edition: "ECE",
                versions: ece,
            },
        ];
        
        // CE is not available to Apps
        if (productType !== "App") {
            version_compatibility.push( { edition: "CE", versions: ce } );
        }
    } else {
        version_compatibility = [
            {
                edition: "CE",
                versions: ce,
            },
            {
                edition: "EE",
                versions: ee,
            },
        ];
    }

    return version_compatibility;
};

// takes in version_compatible form values and the mapping ee, ce, and ece list
export const isProductCloudCompatible = (
    versionCompatiblityFormValues,
    eeList,
    ceList,
    eceList
) => {
    const ece = getVersionCheckboxValues(
        versionCompatiblityFormValues.ECE,
        eceList
    );
    const ee = getVersionCheckboxValues(
        versionCompatiblityFormValues.EE,
        eeList
    );
    const minimumVersion = "2.3";
    const hasMinVersion = versions => {
        if (!versions.length) {
            return false;
        }
        return versions.find(version =>
            COMPARE_VERSIONS(version, minimumVersion, ">=")
        );
    };

    if (hasMinVersion(ece) || hasMinVersion(ee)) {
        return true;
    }
    return false;
};

/**
 * An object of editions and their version list.
 * @typedef {object} EditionLists
 * @property {string[]} eeList - List of versions for the EE editions
 * @property {string[]} eceList - List of versions for the ECE editions
 * @property {string[]} ceList - List of versions for the CE editions
 */
/**
 * An object of editions and their version list.
 * @typedef {object} versionCompatibilityBySubmissionId
 * @property {string} platform - ("M1" | "M2")
 * @property {string} edition - ("CE" | "EE" | "ECE")
 * @property {string[]} versions - version strings such as ["2.1", "2.2", "2.3"]
 */
/**
 * getVersionCompatibilityListByEditions
 * @param  {versionCompatibilityBySubmissionId[]} versionCompatibilityBySubmissionId - list of objects that contain the platform, edition and versions
 * @return {EditionLists} Lists of versions per editions
 */
export const getVersionCompatibilityListByEditions = (
    versionCompatibilityBySubmissionId = []
) => {
    const EE_LIST = "eeList";
    const ECE_LIST = "eceList";
    const CE_LIST = "ceList";
    const editionLists = { [EE_LIST]: [], [ECE_LIST]: [], [CE_LIST]: [] };
    versionCompatibilityBySubmissionId.forEach(editionObj => {
        const edition = editionObj.edition;
        switch (edition) {
            case "EE":
                editionLists[EE_LIST] = editionObj.versions;
                break;
            case "ECE":
                editionLists[ECE_LIST] = editionObj.versions;
                break;
            case "CE":
                editionLists[CE_LIST] = editionObj.versions;
                break;
        }
    });
    return editionLists;
};

export const formatCompatibilityName = version => {
    switch (version) {
        case "EE":
            return MAGENTO_COMMERCE_TYPES.COMMERCE;
        case "ECE":
            return MAGENTO_COMMERCE_TYPES.CLOUD;
        default:
            return MAGENTO_COMMERCE_TYPES.OPEN_SOURCE;
    }
};

export const hasCommercePartnerLevel = (partnerLevel = 15) => {
    return COMMERCE_LEVELS_ARRAY.includes(partnerLevel);
};

export const hasOpenSourceCompatibility = (productType) => {
    return productType !== "app";
};

//make sure product platform is for Magento 2
//also that the current users partner level supports cloud compatibility
//and that there are available items for the cloud compatibility
export const hasCloudCompatibilityAccess = (
    platform,
    partnerLevel,
    versionCompatibilities
) => {
    const bool = !!(
        platform === PRODUCT_PLATFORM.M2 &&
        hasCommercePartnerLevel(partnerLevel) &&
        (versionCompatibilities.ECE && versionCompatibilities.ECE.length)
    );
    
    return bool;
};

export const getDisplayEntitySections = (
    hasOpenSourceAccess,
    hasCommerceAccess,
    hasCloudAccess,
    isInDraft,
    list
) => {
    // list shape from server
    // [
    //     { "edition": "CE", "versions": ["2.2", "2.3"] },
    //     { "edition": "EE", "versions": ["2.2", "2.3"] }
    // ]

    // add EE ECE CE entities if there are any versions selected for that type.
    const listOfEntitiesWithVersions = list.map(entity => {
        const entityType = entity.edition;
        const hasVersions = entity.versions.length;
        if (hasVersions) {
            return entityType;
        }
        return "";
    });

    //conditions on when to show.
    const canShow = (isInDraft, hasAccess, hasVersionsSelected) => {
        if (isInDraft && hasAccess) {
            return true;
        } else if (isInDraft && !hasAccess) {
            return false;
        } else if (!isInDraft && !hasAccess) {
            return false;
        } else if (!isInDraft && hasAccess && !hasVersionsSelected) {
            return false;
        }
        return true;
    };
    const hasCEVersions = listOfEntitiesWithVersions.includes("CE");
    const hasEEVersions = listOfEntitiesWithVersions.includes("EE");
    const hasECEVersions = listOfEntitiesWithVersions.includes("ECE");
    const CE = canShow(isInDraft, hasOpenSourceAccess, hasCEVersions); // CE access is not available to Apps
    const EE = canShow(isInDraft, hasCommerceAccess, hasEEVersions);
    const ECE = canShow(isInDraft, hasCloudAccess, hasECEVersions);
    const displVersionsEntity = hasOpenSourceAccess ? { CE, EE, ECE } : { EE, ECE };
    return displVersionsEntity;
};
